import React, { useEffect, useRef, useState } from 'react';
import styles from './FeedbackButton.module.css';

/** Categories mirror the server's ALLOWED_FEEDBACK_CATEGORIES set in db.mjs.
 *  Keep the keys aligned — the server normalises unknown values to 'wrong' so
 *  a drift here will silently lose category info on the row. */
type FeedbackCategory = 'wrong' | 'missing' | 'verbose' | 'other';

const CATEGORY_OPTIONS: { value: FeedbackCategory; label: string }[] = [
  { value: 'wrong', label: 'Wrong' },
  { value: 'missing', label: 'Missing' },
  { value: 'verbose', label: 'Too wordy' },
  { value: 'other', label: 'Other' },
];

export interface FeedbackButtonProps {
  /** Foreman message id being flagged. Required — freestanding flags use the
   *  /wrong: slash command path, not this button. */
  messageId: string;
  /** Active thread session key. The server enforces ACL membership against it. */
  sessionKey: string | null | undefined;
  /** Mirrors the CorrectionButton convention: shown only on row hover. */
  visible?: boolean;
}

/** Posts the flag to /api/auth/feedback/report. Caller-side validation is
 *  intentionally light — the server rejects empty/too-short reason. We keep
 *  the inline UX optimistic: show "flagged" on 2xx, "couldn't flag" on
 *  anything else. */
async function postFeedback(payload: {
  sessionKey: string;
  messageId: string;
  category: FeedbackCategory;
  reason: string;
  suggestedFix?: string;
}): Promise<boolean> {
  const token = localStorage.getItem('foreman-auth-token') || '';
  try {
    const res = await fetch('/api/auth/feedback/report', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export const FeedbackButton: React.FC<FeedbackButtonProps> = ({
  messageId,
  sessionKey,
  visible = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState<FeedbackCategory>('wrong');
  const [reason, setReason] = useState('');
  const [suggested, setSuggested] = useState('');
  const [submitting, setSubmitting] = useState(false);
  /** 'sent' shows the inline confirmation; 'error' shows the inline retry hint.
   *  Both auto-clear after 3 seconds. */
  const [flash, setFlash] = useState<null | 'sent' | 'error'>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Escape closes the modal. Mirrors CorrectionButton's pattern so the two
  // affordances feel the same.
  useEffect(() => {
    if (!isOpen) return;
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [isOpen]);

  // Clear the auto-flash timer on unmount so we don't setState a dead node.
  useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    },
    []
  );

  const triggerFlash = (kind: 'sent' | 'error') => {
    setFlash(kind);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), 3000);
  };

  const isValid = reason.trim().length >= 5 && reason.trim().length <= 1000;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || submitting) return;
    if (!sessionKey) {
      // Shouldn't happen — ChatArea only renders this when activeSessionId is
      // truthy — but fail soft.
      triggerFlash('error');
      return;
    }
    setSubmitting(true);
    const ok = await postFeedback({
      sessionKey,
      messageId,
      category,
      reason: reason.trim(),
      suggestedFix: suggested.trim() || undefined,
    });
    setSubmitting(false);
    if (ok) {
      setIsOpen(false);
      setReason('');
      setSuggested('');
      setCategory('wrong');
      triggerFlash('sent');
    } else {
      triggerFlash('error');
    }
  };

  return (
    <div className={styles.wrapper}>
      <button
        className={`${styles.button} ${visible || isOpen ? styles.visible : ''}`.trim()}
        onClick={() => setIsOpen((prev) => !prev)}
        type="button"
        aria-label="Flag this response as wrong"
        title="Flag this response"
      >
        <span className={styles.icon}>{'✕'}</span>
        Flag
      </button>

      {flash === 'sent' && (
        <span className={styles.flashOk} role="status">
          {'✓ Flagged. Martin will see this.'}
        </span>
      )}
      {flash === 'error' && (
        <span className={styles.flashErr} role="status">
          {'Couldn’t flag — try again'}
        </span>
      )}

      {isOpen && (
        <>
          <div className={styles.overlay} onClick={() => setIsOpen(false)} />
          <div className={styles.formContainer}>
            <div className={styles.formHeader}>
              <span className={styles.formTitle}>
                <span className={styles.formTitleIcon}>{'✕'}</span>
                Flag this response
              </span>
              <button
                type="button"
                className={styles.closeButton}
                onClick={() => setIsOpen(false)}
                aria-label="Close flag form"
              >
                {'✕'}
              </button>
            </div>

            <form className={styles.form} onSubmit={handleSubmit}>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>What was wrong?</label>
                <div className={styles.categoryRow}>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`${styles.categoryChip} ${
                        category === opt.value ? styles.categoryChipActive : ''
                      }`}
                      onClick={() => setCategory(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.label}>
                  Tell Martin what's wrong <span className={styles.required}>*</span>
                </label>
                <textarea
                  className={styles.textarea}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. The cabinet count is off — Mullins has 14 boxes, not 12."
                  maxLength={1000}
                  rows={3}
                  autoFocus
                />
                <div className={styles.hint}>
                  {reason.trim().length}/1000
                  {reason.trim().length > 0 && reason.trim().length < 5 ? ' — need at least 5 chars' : ''}
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.label}>What should it have said? (optional)</label>
                <textarea
                  className={styles.textarea}
                  value={suggested}
                  onChange={(e) => setSuggested(e.target.value)}
                  placeholder="The right answer, or a pointer to where the right answer lives."
                  maxLength={2000}
                  rows={2}
                />
              </div>

              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={() => setIsOpen(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.submitBtn}
                  disabled={!isValid || submitting}
                >
                  {submitting ? 'Sending…' : 'Send flag'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};
